import React from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Heading from "@components/layout/Heading";
import Table from "@components/layout/Table";
import Tag from "@components/shared/Tag";
import ConfirmModal from "@components/shared/ConfirmModal";
import { toastError, toastInfo, toastSuccess } from "@utils/toast";
import { useAppContext } from "@context/AppContext";
import { useSubscriberSyncUpload } from "@context/subscriberSyncUpload/Provider";
import {
  UploadRow,
  canSubmit,
  RowStatus,
} from "@context/subscriberSyncUpload/reducer";
import { useFinalizeSubscriberSync } from "@hooks/mutation/useFinalizeSubscriberSync";
import { useReclassifySubscriberSync } from "@hooks/mutation/useReclassifySubscriberSync";
import { useUpdateSubscriberSyncRow } from "@hooks/mutation/useUpdateSubscriberSyncRow";
import { useUpdateSubscriberSyncRowNote } from "@hooks/mutation/useUpdateSubscriberSyncRowNote";
import { useBulkDecideSubscriberSyncRows } from "@hooks/mutation/useBulkDecideSubscriberSyncRows";
import { useDeleteSubscriberSyncRow } from "@hooks/mutation/useDeleteSubscriberSyncRow";
import { RowDecisionInput, ReclassifyResult, ROW_STATUS_FAILED } from "@services/subscriberSyncUpload";
import RowDetailModal from "./RowDetailModal";
import RowNoteCell from "./RowNoteCell";
import { STATUS_TAG, renderRowDiffs } from "./rowDisplay";

const ACTIONABLE_STATUSES: RowStatus[] = [
  "has_differences",
  "not_found",
  "invalid",
];

// Backend SubscriberSyncUploadRowStatus.Success — a row already committed to SEQWL. On a resumed
// partial/completed upload these rows come back still classified has_differences, so we treat
// "ledgered" as a display state in its own right: shown as Ledgered, read-only, never re-decided.
const ROW_STATUS_SUCCESS = 1;
const isLedgered = (row: UploadRow) => row.status === ROW_STATUS_SUCCESS;

const DECISION_TAG: Record<
  UploadRow["decision"],
  { status: "success" | "error" | "warning"; key: string }
> = {
  pending: {
    status: "warning",
    key: "subscriber_sync_upload.decision.pending",
  },
  approved: {
    status: "success",
    key: "subscriber_sync_upload.decision.approved",
  },
  declined: {
    status: "error",
    key: "subscriber_sync_upload.decision.declined",
  },
};

const Review = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: level1ResourceId = "" } = useParams();
  const { state, dispatch } = useSubscriberSyncUpload();
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [detailRow, setDetailRow] = React.useState<UploadRow | null>(null);
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  // Drives the post-submit acknowledgment modal. Closing it exits via handleCancel;
  // the detached finalize runs in the background and notifies the operator when done.
  const [syncStarted, setSyncStarted] = React.useState(false);

  const finalizeMutation = useFinalizeSubscriberSync({
    onSuccess: () => setSyncStarted(true),
    onError: (err: any) =>
      toastError(
        t("subscriber_sync_upload.message.error", {
          error: err?.response?.data?.message ?? err?.message ?? "unknown",
        }),
      ),
  });

  // Re-checks just the rows that finalized Failed (STALE drift or commit errors) against current
  // live data and re-enters Review with the freshly-classified rows. Reachable when a
  // partially-completed upload is reopened from the history list (?draftId=...).
  const reclassifyMutation = useReclassifySubscriberSync({
    onSuccess: (data: ReclassifyResult) => {
      dispatch({ type: "trial-run-loaded", uploadId: data.uploadId, rows: data.rows });
    },
    onError: (err: any) =>
      toastError(
        t("subscriber_sync_upload.message.error", {
          error: err?.response?.data?.message ?? err?.message ?? "unknown",
        }),
      ),
  });

  // Decisions must be persisted server-side before finalize — `ledger` rejects
  // any has_differences row still `pending`. Persist first, then mirror into
  // local state on success so the table + canSubmit reflect the saved decision.
  const decideError = (err: any) =>
    toastError(
      t("subscriber_sync_upload.message.error", {
        error: err?.response?.data?.message ?? err?.message ?? "unknown",
      }),
    );

  // Confirm each decision with a toast — the row's Approved/Declined tag can be off-screen when the
  // operator is filtered to another view (or acting from the detail modal), so the toast is the
  // immediate, always-visible feedback that the decision was saved.
  const decideRowMutation = useUpdateSubscriberSyncRow({
    onSuccess: (
      _data: unknown,
      variables: { rowId: string; decision: RowDecisionInput },
    ) => {
      dispatch({
        type: "decide-row",
        rowId: variables.rowId,
        decision: variables.decision,
      });
      const account =
        state.rows.find(r => r.id === variables.rowId)?.accountNumber ?? "";
      toastSuccess(
        t(`subscriber_sync_upload.message.row_${variables.decision}`, {
          account,
        }),
      );
    },
    onError: decideError,
  });

  const bulkDecideMutation = useBulkDecideSubscriberSyncRows({
    onSuccess: (
      _data: unknown,
      variables: { decision: RowDecisionInput; rowIds: string[] },
    ) => {
      // Mirror the request's scope in local state + toast — the server only flipped these rowIds
      // (ledgered rows on a resumed PartiallyCompleted upload are refused), so a broader flip
      // would show a decision that a reload would immediately undo.
      dispatch({
        type: "bulk-decide",
        decision: variables.decision,
        rowIds: variables.rowIds,
      });
      toastSuccess(
        t(`subscriber_sync_upload.message.bulk_${variables.decision}`, {
          count: variables.rowIds.length,
        }),
      );
    },
    onError: decideError,
  });

  // Row-level delete from the review grid — mirrors bulk meter upload's trial-run row delete. The
  // backend rejects deleting an already-ledgered row. A confirmation toast fires on success so the
  // operator gets feedback even when the row was off-screen due to the actionable filter.
  const deleteRowMutation = useDeleteSubscriberSyncRow({
    onSuccess: (_data: unknown, variables: { rowId: string }) => {
      const account =
        state.rows.find(r => r.id === variables.rowId)?.accountNumber ?? "";
      dispatch({ type: "remove-row", rowId: variables.rowId });
      toastSuccess(
        t("subscriber_sync_upload.message.row_deleted", { account }),
      );
    },
    onError: decideError,
  });

  const handleDeleteRow = async () => {
    if (!state.uploadId || !deletingRowId) return;
    try {
      await deleteRowMutation.mutateAsync({
        uploadId: state.uploadId,
        rowId: deletingRowId,
      });
    } catch {
      // onError surfaces the toast; just close the confirm.
    } finally {
      setDeletingRowId(null);
    }
  };

  // Per-row notes are edited inline in the grid's Note column (the detail modal is comparison-only).
  // Gated on CreateNotes; only matched rows (in_sync / has_differences) can anchor a note server-side.
  const { checkPermissions } = useAppContext();
  const canCreateNotes = checkPermissions(["CreateNotes"]);

  const updateNoteMutation = useUpdateSubscriberSyncRowNote({
    onSuccess: (_data: unknown, variables: { rowId: string; note: string }) => {
      dispatch({
        type: "update-row-note",
        rowId: variables.rowId,
        note: variables.note,
      });
      toastSuccess(t("subscriber_sync_upload.modal.note_saved"));
    },
    onError: decideError,
  });

  const saveNote = (rowId: string, note: string) => {
    if (!state.uploadId) return;
    updateNoteMutation.mutate({ uploadId: state.uploadId, rowId, note });
  };

  const decide = (rowId: string, decision: RowDecisionInput) => {
    if (!state.uploadId) return;
    decideRowMutation.mutate({ uploadId: state.uploadId, rowId, decision });
  };

  const bulkDecide = (decision: RowDecisionInput) => {
    if (!state.uploadId) return;
    // Only flip rows whose current decision differs from the target — eliminates a no-op network
    // call when the operator clicks "Approve all" on an already-all-approved file, and lets us
    // surface a clear "nothing to do" toast instead of silently doing nothing (the previous
    // behaviour: an empty rowIds list returned without any feedback).
    const rowIds = state.rows
      .filter(
        row =>
          row.rowStatus === "has_differences" &&
          !isLedgered(row) &&
          row.decision !== decision,
      )
      .map(row => row.id);
    if (rowIds.length === 0) {
      toastInfo(
        t(
          decision === "approved"
            ? "subscriber_sync_upload.message.bulk_nothing_to_approve"
            : "subscriber_sync_upload.message.bulk_nothing_to_decline",
        ),
      );
      return;
    }
    bulkDecideMutation.mutate({ uploadId: state.uploadId, decision, rowIds });
  };

  const decideDisabled =
    decideRowMutation.isLoading || bulkDecideMutation.isLoading;

  const visibleRows = state.rows.filter(row =>
    state.filter === "all" ? true : ACTIONABLE_STATUSES.includes(row.rowStatus),
  );

  const failedRowIds = state.rows
    .filter(row => row.status === ROW_STATUS_FAILED)
    .map(row => row.id);

  const handleSubmit = () => {
    if (!state.uploadId) return;
    // FR-6: the session note entered on the upload step (NoteComposer) is committed at finalize.
    finalizeMutation.mutate({
      uploadId: state.uploadId,
      note: state.sessionNote.body,
      isReportable: state.sessionNote.isReportable,
    });
    setShowConfirm(false);
  };

  // Cancel = leave the wizard WITHOUT deleting the draft (mirrors bulk meter upload's handleCancel,
  // which only navigates away). The draft was created eagerly on entering Review and every
  // decision/note is already persisted server-side, so it stays as a Draft and shows in the upload
  // list to resume or delete later. Deleting a draft is done explicitly from that list's Delete action.
  const handleCancel = () => {
    dispatch({ type: "reset" });
    navigate(
      level1ResourceId
        ? `/polestar/level1wrs/${level1ResourceId}`
        : "/polestar/subscribers/sync_uploads",
    );
  };

  // Save as draft: mirrors bulk meter's "Save as draft" — confirm with a toast and stay on the page
  // (it does not navigate away or reset). The record and every decision/note are already persisted
  // server-side as a Draft (each approve/decline/note commits immediately), so the operator can leave
  // via the breadcrumb/menu whenever and resume from the upload list. Distinct from Cancel, which
  // deletes the draft.
  const handleSaveDraft = () => {
    toastSuccess(t("bulk_meter_upload.message.draft_saved"));
  };

  const fields = [
    {
      title: t("subscriber_sync_upload.table.account_number"),
      name: "accountNumber",
    },
    {
      title: t("subscriber_sync_upload.table.customer_name"),
      name: "customerName",
    },
    { title: t("subscriber_sync_upload.table.status"), name: "rowStatus" },
    {
      title: t("subscriber_sync_upload.table.differences"),
      name: "differences",
    },
    { title: t("subscriber_sync_upload.table.note"), name: "note" },
    { title: t("subscriber_sync_upload.table.decision"), name: "decision" },
    { title: t("common.action"), name: "actions" },
  ];

  const tableData = visibleRows.map(row => ({
    accountNumber: row.accountNumber,
    customerName: row.customerName,
    rowStatus: isLedgered(row) ? (
      <Tag status="success">
        {t("subscriber_sync_upload.row_status.ledgered")}
      </Tag>
    ) : (
      <Tag status={STATUS_TAG[row.rowStatus].status}>
        {t(STATUS_TAG[row.rowStatus].key)}
      </Tag>
    ),
    differences: renderRowDiffs(row, t),
    note: (() => {
      // UAT TC-14: no note on declined / unmatched rows — nothing is committed so there is no
      // customer record to anchor the note to. Batch rationale lives in the session note (TC-15).
      // Declined rows hide both the editor AND any previously-saved note text (the row's note
      // record stays as audit-only data, but is never promoted to a customer note).
      const isDeclined = row.decision === "declined";
      if (canCreateNotes && row.matchedSubscriberId && !isDeclined) {
        return (
          <span data-testid={`note-${row.id}`}>
            <RowNoteCell
              note={row.note}
              saving={updateNoteMutation.isLoading}
              onSave={note => saveNote(row.id, note)}
            />
          </span>
        );
      }
      const display = isDeclined ? "" : row.note;
      return (
        <span
          className="block max-w-[16rem] truncate text-xs text-gray-700"
          title={display || undefined}
          data-testid={`note-${row.id}`}
        >
          {display || "—"}
        </span>
      );
    })(),
    decision: (
      // Only has_differences rows carry a decision; in_sync / not_found / invalid are skipped
      // automatically, so showing a Pending/Approved tag for them is misleading.
      <span data-testid={`decision-${row.id}`}>
        {row.rowStatus === "has_differences" ? (
          <Tag status={DECISION_TAG[row.decision].status}>
            {t(DECISION_TAG[row.decision].key)}
          </Tag>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </span>
    ),
    actions: (() => {
      // Row actions dropdown. Order: Details (always top, stable for muscle memory) → per-row
      // Approve/Decline (only on has_differences rows that haven't been ledgered) → Delete.
      // Approve/Decline mirror the bulk buttons and reuse decideRowMutation; the current decision
      // is omitted from its own menu (clicking "Approve" on an already-approved row would be a
      // confusing no-op — the Decision pill in the previous column is the source of truth for the
      // current state). Delete is hidden once a row has been ledgered (Success), matching bulk
      // meter upload, and the backend also rejects deleting a ledgered row.
      const isLedgered = row.status === 1;
      const isDecidable = row.rowStatus === "has_differences" && !isLedgered;
      return (
        <Select
          className="subscriber_sync_row_actions"
          classNamePrefix="subscriber_sync_row_actions"
          placeholder={t("common.actions")}
          options={[
            {
              label: t("subscriber_sync_upload.modal.details_button"),
              value: "details",
            },
            ...(isDecidable && row.decision !== "approved"
              ? [{ label: t("common.approve"), value: "approve" }]
              : []),
            ...(isDecidable && row.decision !== "declined"
              ? [
                  {
                    label: t("subscriber_sync_upload.decline"),
                    value: "decline",
                  },
                ]
              : []),
            ...(!isLedgered
              ? [{ label: t("common.delete"), value: "delete" }]
              : []),
          ]}
          onChange={(e: any) => {
            if (e?.value === "details") setDetailRow(row);
            else if (e?.value === "approve") decide(row.id, "approved");
            else if (e?.value === "decline") decide(row.id, "declined");
            else if (e?.value === "delete") setDeletingRowId(row.id);
          }}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          isSearchable={false}
          value={null}
        />
      );
    })(),
  }));

  const submitDisabled = !canSubmit(state) || finalizeMutation.isLoading;

  return (
    <>
      <div className="flex flex-col grow gap-6 p-6">
        <div className="flex justify-between items-center gap-4 flex-wrap">
          <Heading light>
            {/* Heading reflects which set the operator is currently viewing — the count alone is
                ambiguous when the actionable filter toggles between 11 and 20. */}
            {t("subscriber_sync_upload.title.upload")} —{" "}
            {t(
              state.filter === "actionable"
                ? "subscriber_sync_upload.message.count_actionable"
                : "subscriber_sync_upload.message.count_total",
              { count: visibleRows.length },
            )}
          </Heading>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-outline-primary text-sm rounded"
              disabled={decideDisabled}
              onClick={() => bulkDecide("approved")}
            >
              {t("subscriber_sync_upload.review.approve_all")}
            </button>
            <button
              type="button"
              className="btn-outline-primary text-sm rounded"
              disabled={decideDisabled}
              onClick={() => bulkDecide("declined")}
            >
              {t("subscriber_sync_upload.review.decline_all")}
            </button>
            {failedRowIds.length > 0 && (
              <button
                type="button"
                className="btn-outline-primary text-sm rounded"
                disabled={reclassifyMutation.isLoading}
                onClick={() => state.uploadId && reclassifyMutation.mutate({ uploadId: state.uploadId, rowIds: failedRowIds })}
              >
                {t("subscriber_sync_upload.summary.retry_skipped", { count: failedRowIds.length })}
              </button>
            )}
            <button
              type="button"
              className="btn-outline-primary text-sm rounded"
              onClick={() =>
                dispatch({
                  type: "set-filter",
                  filter: state.filter === "actionable" ? "all" : "actionable",
                })
              }
            >
              {state.filter === "actionable"
                ? t("subscriber_sync_upload.review.show_all")
                : t("subscriber_sync_upload.review.show_actionable")}
            </button>
          </div>
        </div>

        <Table
          fields={fields}
          data={tableData}
          loading={finalizeMutation.isLoading}
          stickyHeader
        />
      </div>

      <footer className="flex gap-4 border-t border-gray-300 p-6">
        <button
          type="button"
          className="btn-primary"
          disabled={submitDisabled}
          onClick={() => setShowConfirm(true)}
        >
          {t("common.submit")}
        </button>
        <button
          type="button"
          className="btn-outline-primary"
          disabled={finalizeMutation.isLoading}
          onClick={handleSaveDraft}
        >
          {t("bulk_meter_upload.save_as_draft")}
        </button>
        <button
          type="button"
          className="btn-outline-primary"
          onClick={handleCancel}
        >
          {t("common.cancel")}
        </button>
      </footer>

      <ConfirmModal
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleSubmit}
        isSubmitting={finalizeMutation.isLoading}
      >
        {t("subscriber_sync_upload.confirm_message")}
      </ConfirmModal>

      <ConfirmModal
        open={!!deletingRowId}
        onClose={() => setDeletingRowId(null)}
        onConfirm={handleDeleteRow}
        isSubmitting={deleteRowMutation.isLoading}
        confirmText={t("common.delete")}
      >
        {t("bulk_meter_upload.delete_row_confirm")}
      </ConfirmModal>

      {/* Post-submit acknowledgment: the sync runs detached; the operator is notified when it
          completes. Close is the only exit and leaves the wizard via handleCancel. */}
      <ConfirmModal
        open={syncStarted}
        onConfirm={handleCancel}
        confirmText={t("common.close")}
      >
        {t("subscriber_sync_upload.message.sync_started")}
      </ConfirmModal>

      {detailRow ? (
        <RowDetailModal
          row={detailRow}
          deciding={decideDisabled}
          onDecide={decision => {
            decide(detailRow.id, decision);
            setDetailRow(null);
          }}
          onClose={() => setDetailRow(null)}
        />
      ) : null}
    </>
  );
};

export default Review;
